import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, {useEffect, useState} from "react";
import {UI, rgba} from "../../uiTokens";
import {fieldSx, ghostBtnSx, primaryBtnSx, sectionLabelSx} from "../editMenuStyles";

const VarType = {
  CONST: "CONST",
  TRANSFER: "TRANSFER",
};

const initParam = {
  source_scope: "const",
  source: "",
  target_scope: "flow",
  scope: "flow",
  target: "",
};

const TypeCard = ({active, title, description, onClick}) => (
  <Box
    onClick={onClick}
    sx={{
      flex: 1,
      p: 1,
      borderRadius: "8px",
      cursor: "pointer",
      border: `1px solid ${active ? UI.accent : rgba(UI.ink, 0.15)}`,
      backgroundColor: active ? rgba(UI.accent, 0.06) : UI.surface,
      transition: "border-color .12s ease, background-color .12s ease",
      "&:hover": {borderColor: active ? UI.accent : rgba(UI.accent, 0.4)},
    }}
  >
    <Typography sx={{fontSize: 12.5, fontWeight: 700, color: active ? UI.accent : UI.ink}}>{title}</Typography>
    <Typography sx={{mt: 0.25, fontSize: 10.5, color: UI.muted, lineHeight: 1.3}}>{description}</Typography>
  </Box>
);

export const VarForm = ({selectedParam, isForm, project, handleClose, onSubmit, onDelete, variableOptions = []}) => {
  const [type, setType] = useState(VarType.CONST);
  const [param, setParam] = useState({...initParam});

  useEffect(() => {
    if (selectedParam) {
      setType(selectedParam?.source_scope === "const" ? VarType.CONST : VarType.TRANSFER);
      setParam(selectedParam);
    } else {
      setParam({...initParam});
    }
  }, [selectedParam]);

  useEffect(() => {
    setParam(prev => ({...prev, source_scope: type === VarType.TRANSFER ? "flow" : "const"}));
  }, [type]);

  const onClose = () => {
    setParam({...initParam});
    setType(VarType.CONST);
    handleClose();
  };

  return (
    <Stack gap={2}>
      <Box>
        <Typography sx={{...sectionLabelSx, mb: 0.75}}>Write to</Typography>
        <Stack direction="row" gap={1}>
          <ScopeSelect
            value={param?.target_scope}
            name="target_scope"
            isForm={isForm}
            onChange={(e) => {
              setParam(prev => {
                const params = {...prev};
                params.target_scope = e.target.value;
                params.scope = e.target.value;
                return params;
              });
            }}
          />
          <TextField
            size="small"
            label="Target"
            sx={{flex: 1, ...fieldSx}}
            value={param?.target || ""}
            onChange={(e) => {
              setParam(prev => ({...prev, target: e.target.value}));
            }}
          />
        </Stack>
      </Box>

      <Box>
        <Typography sx={{...sectionLabelSx, mb: 0.75}}>Value from</Typography>
        <Stack direction="row" gap={1}>
          <TypeCard
            active={type === VarType.CONST}
            title="Constant"
            description="Set the variable from a project constant"
            onClick={() => setType(VarType.CONST)}
          />
          <TypeCard
            active={type === VarType.TRANSFER}
            title="Transfer"
            description="Move a variable from one scope to another"
            onClick={() => setType(VarType.TRANSFER)}
          />
        </Stack>
      </Box>

      {type === VarType.TRANSFER ? (
        <Stack direction="row" gap={1}>
          <ScopeSelect
            value={param?.source_scope}
            name="source_scope"
            isForm={isForm}
            onChange={(e) => {
              setParam(prev => ({...prev, source_scope: e.target.value}));
            }}
          />
          <Autocomplete
            freeSolo
            options={variableOptions}
            inputValue={param?.source || ""}
            onInputChange={(e, val) => setParam(prev => ({...prev, source: val}))}
            sx={{flex: 1}}
            slotProps={{
              popper: {placement: "bottom-start", modifiers: [{name: "flip", enabled: false}]},
              paper: {
                sx: {
                  borderRadius: "10px",
                  border: `1px solid ${rgba(UI.accent, 0.16)}`,
                  boxShadow: UI.shadow,
                },
              },
            }}
            ListboxProps={{style: {maxHeight: 200, overflowY: "auto"}}}
            renderInput={(params) => <TextField {...params} size="small" label="Source variable" sx={fieldSx}/>}
          />
        </Stack>
      ) : (
        <FormControl size="small" fullWidth sx={fieldSx}>
          <InputLabel id="constant-label">Constant</InputLabel>
          <Select
            labelId="constant-label"
            size="small"
            label="Constant"
            value={param?.source}
            onChange={(e) => {
              setParam(prev => ({...prev, source: e.target.value}));
            }}
          >
            {project?.constants &&
              Object?.entries(project?.constants)?.map(([key, value]) => (
                <MenuItem key={key} value={key}>{key} — {value}</MenuItem>
              ))}
          </Select>
        </FormControl>
      )}

      <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" sx={{pt: 0.5}}>
        <Box>
          {selectedParam && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              sx={{textTransform: "none", borderRadius: "8px"}}
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
        </Box>
        <Stack direction="row" gap={1}>
          <Button size="small" sx={ghostBtnSx} onClick={onClose}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            sx={primaryBtnSx}
            disabled={!param?.source || !param?.target}
            onClick={() => onSubmit(param)}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

const ScopeSelect = ({value, onChange, isForm = false, name}) => (
  <FormControl size="small" sx={{flex: 1, ...fieldSx}}>
    <InputLabel id={`${name}-scope-label`}>Scope</InputLabel>
    <Select
      labelId={`${name}-scope-label`}
      size="small"
      name={name}
      label="Scope"
      value={value}
      onChange={onChange}
    >
      {isForm && <MenuItem value="form">Form</MenuItem>}
      <MenuItem value="flow">Flow</MenuItem>
      <MenuItem value="global">Global</MenuItem>
    </Select>
  </FormControl>
);
